import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { ICartaoBanco, IContaBancaria, ISalvarContaBancaria, ISugestaoContaBancaria } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';

interface Props {
    show: boolean;
    empresaId: number;
    esteiraId: number;
    /** Edição de uma conta já cadastrada. */
    conta?: IContaBancaria | null;
    /** Cadastro a partir da conta lida no OFX: grava a conta e liga o arquivo a ela. */
    sugestao?: { arquivoId: number; dados: ISugestaoContaBancaria } | null;
    onHide: () => void;
    onSalvo: () => void;
}

// DEVN3-767 — cadastro de conta bancária. A conta contábil só pode ser um cartão de Banco/Caixa da
// esteira; depois que a conta tem extrato aceito, só descrição e situação podem mudar.
export default function ModalContaBancaria({ show, empresaId, esteiraId, conta, sugestao, onHide, onSalvo }: Props) {
    const [cartoes, setCartoes] = useState<ICartaoBanco[]>([]);
    const [salvando, setSalvando] = useState(false);
    const [form, setForm] = useState<ISalvarContaBancaria>({
        empresaId, esteiraId, banco: '', agencia: '', conta: '', contaContabil: '', descricao: '', ativo: true,
    });

    useEffect(() => {
        if (!show) return;
        setForm({
            empresaId,
            esteiraId,
            banco: conta?.banco ?? sugestao?.dados.banco ?? '',
            agencia: conta?.agencia ?? sugestao?.dados.agencia ?? '',
            conta: conta?.conta ?? sugestao?.dados.conta ?? '',
            contaContabil: conta?.contaContabil ?? sugestao?.dados.contaContabil ?? '',
            descricao: conta?.descricao ?? '',
            ativo: conta?.ativo ?? true,
        });
        ModuloBancarioService.cartoesBanco(esteiraId).then(setCartoes).catch(() => setCartoes([]));
    }, [show, empresaId, esteiraId, conta, sugestao]);

    const alterar = (campo: keyof ISalvarContaBancaria, valor: string | boolean) =>
        setForm(f => ({ ...f, [campo]: valor }));

    const salvar = async () => {
        setSalvando(true);
        try {
            if (conta) await ModuloBancarioService.atualizarConta(conta.id, form);
            else if (sugestao) await ModuloBancarioService.aceitarSugestao(sugestao.arquivoId, form);
            else await ModuloBancarioService.criarConta(form);

            Swal.fire({ text: 'Conta bancária salva.', icon: 'success', timer: 2000, showConfirmButton: false });
            onSalvo();
            onHide();
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível salvar a conta bancária.', icon: 'error' });
        } finally {
            setSalvando(false);
        }
    };

    const titulo = conta ? 'Editar conta bancária' : sugestao ? 'Cadastrar conta do extrato' : 'Nova conta bancária';

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{titulo}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {sugestao && (
                    <div className='alert alert-light-primary py-2 mb-4'>
                        Dados lidos do arquivo. Confira a agência e a conta: alguns bancos juntam as duas no mesmo campo.
                    </div>
                )}
                <div className='row g-3'>
                    <div className='col-4'>
                        <label className='form-label required'>Banco</label>
                        <input className='form-control' value={form.banco} placeholder='341'
                            onChange={e => alterar('banco', e.target.value)} />
                    </div>
                    <div className='col-4'>
                        <label className='form-label'>Agência</label>
                        <input className='form-control' value={form.agencia}
                            onChange={e => alterar('agencia', e.target.value)} />
                    </div>
                    <div className='col-4'>
                        <label className='form-label required'>Conta</label>
                        <input className='form-control' value={form.conta} placeholder='com dígito'
                            onChange={e => alterar('conta', e.target.value)} />
                    </div>
                    <div className='col-12'>
                        <label className='form-label required'>Conta contábil</label>
                        <select className='form-select' value={form.contaContabil}
                            onChange={e => alterar('contaContabil', e.target.value)}>
                            <option value=''>Selecione a conta de Banco/Caixa</option>
                            {cartoes.map(c => (
                                <option key={c.id} value={c.contaEmpresa}>
                                    {c.contaEmpresa}{c.descricao ? ` — ${c.descricao}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='col-12'>
                        <label className='form-label'>Descrição</label>
                        <input className='form-control' value={form.descricao} placeholder='Ex.: Itaú movimento'
                            onChange={e => alterar('descricao', e.target.value)} />
                    </div>
                    {conta && (
                        <div className='col-12'>
                            <label className='form-check form-check-custom form-check-solid'>
                                <input className='form-check-input' type='checkbox' checked={form.ativo}
                                    onChange={e => alterar('ativo', e.target.checked)} />
                                <span className='form-check-label'>Ativa</span>
                            </label>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className='btn btn-light' onClick={onHide} disabled={salvando}>Cancelar</button>
                <button className='btn btn-primary' onClick={salvar} disabled={salvando}>
                    {salvando && <span className='spinner-border spinner-border-sm me-2'></span>}
                    Salvar
                </button>
            </Modal.Footer>
        </Modal>
    );
}
